/**
 * Grounding Tool - Cluster Reply Showcase
 *
 * Reports which knowledge base sources were used in a response (citation).
 * Uses the same Cluster Reply data layer as the search tool.
 */

import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';
import { getClusterReplyChunkById } from '@/data/cluster-reply';

// Chunk IDs may contain # (e.g. file#section-slug)
const KEY_PATTERN = /^[a-zA-Z0-9_=\-#]+$/;

export const groundingToolClusterReplySchema: ToolSchema = {
  type: 'function',
  name: 'report_grounding',
  description:
    'Report which sources from the Cluster Reply knowledge base were used in your answer (cite the source). ' +
    'Source IDs appear in square brackets before each passage. Always use this tool to cite sources when answering from the knowledge base.',
  parameters: {
    type: 'object',
    properties: {
      sources: {
        type: 'array',
        items: {
          type: 'string',
          description: 'Source ID from the knowledge base'
        },
        description:
          'List of source IDs actually used in the last response; do not include unused sources'
      }
    },
    required: ['sources'],
    additionalProperties: false
  }
};

export const groundingToolClusterReplyImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  try {
    const { sources } = args;

    if (!Array.isArray(sources)) {
      return {
        success: false,
        error: 'Invalid sources parameter. Expected an array of source IDs.'
      };
    }

    const validatedSources = sources.filter(
      (source) => typeof source === 'string' && KEY_PATTERN.test(source)
    );

    if (validatedSources.length === 0) {
      console.warn('⚠️ No valid Cluster Reply sources provided for grounding');
      return { success: true, data: { sources: [] } };
    }

    console.log(`📚 Grounding Cluster Reply sources: ${validatedSources.join(', ')}`);

    const citationData = validatedSources
      .map((sourceId) => {
        const chunk = getClusterReplyChunkById(sourceId);
        if (!chunk) return null;
        return {
          chunk_id: sourceId,
          title: chunk.title,
          chunk: chunk.content.length > 500 ? chunk.content.slice(0, 500) + '…' : chunk.content
        };
      })
      .filter(Boolean) as Array<{ chunk_id: string; title: string; chunk: string }>;

    if (citationData.length === 0) {
      console.warn('⚠️ No valid Cluster Reply sources found in knowledge base');
      return { success: true, data: { sources: [] } };
    }

    console.log(`✅ Cited ${citationData.length} Cluster Reply sources`);

    return {
      success: true,
      data: { sources: citationData }
    };
  } catch (error) {
    console.error('❌ Cluster Reply grounding tool error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during grounding'
    };
  }
};
